import { useCallback, useEffect, useMemo, useState } from "react";
import { buildChromeIntentUrl, detectEnvironment, type Environment } from "./env";
import { useInstallPrompt } from "./useInstallPrompt";
import { useInstalledApp } from "./useInstalledApp";

export type NotificationPermissionState = "default" | "granted" | "denied" | "unsupported";

/**
 * 지금 사용자가 해야 할 단 하나의 행동.
 *
 * 순서가 중요하다 — 브라우저 정규화(switch-browser)가 설치·권한보다 반드시 먼저다.
 * 브라우저마다 서비스워커·푸시 구독·FCM 토큰이 전부 별개라, 순서가 뒤바뀌면
 * 여러 브라우저에 구독이 생겨 같은 이벤트를 중복 수신하게 된다. (#176)
 */
export type OnboardingStep =
  /** 더 할 일이 없다 (설치·알림 완료, 또는 이 환경에서 가능한 최선에 도달) */
  | { kind: "done" }
  /** 이 OS 에서 PWA 가 온전히 동작하는 브라우저로 이동해야 한다 */
  | { kind: "switch-browser"; target: "chrome" | "safari"; openInChrome?: () => void }
  /** 홈 화면에 추가해야 한다. iOS 는 수동(공유 메뉴) */
  | { kind: "install"; manual: boolean; promptInstall?: () => Promise<void> }
  /**
   * 설치된 앱이 있는데 브라우저 탭으로 실행 중이다. 앱으로 옮겨가야 한다.
   *
   * 브라우저에서 설치된 PWA 를 실행시키는 API 는 없다 — 안내가 유일한 수단이다.
   * `justInstalled` 는 방금 설치를 마친 경우로, 문구를 "설치 완료"로 바꾸기 위한 것.
   */
  | { kind: "open-app"; justInstalled: boolean }
  /** 알림 권한을 요청해야 한다 */
  | { kind: "enable-notification"; request: () => Promise<void> }
  /**
   * 이 환경에서는 푸시를 받을 수 없다. 반드시 사용자에게 명시해야 한다 —
   * "못 받는 사람이 그 사실을 모르는 것"이 안전상 가장 위험하다.
   */
  | {
      kind: "push-unavailable";
      reason: "denied" | "no-push-service" | "unsupported-browser";
    };

function readPermission(env: Environment): NotificationPermissionState {
  if (!env.supportsNotification) return "unsupported";
  return Notification.permission as NotificationPermissionState;
}

export interface UseOnboardingOptions {
  /**
   * 실제로 푸시 토큰을 발급받을 수 있는지 확인하는 프로브.
   * 앱마다 FCM 배관이 다르므로 주입받는다.
   *
   * Play 서비스(GMS) 미탑재 기기는 권한을 허용해도 토큰 발급이 실패한다.
   * 이 경우를 잡아내기 위한 것이며, 실패 시 `push-unavailable` 로 떨어진다.
   */
  pushProbe?: () => Promise<boolean>;
}

export function useOnboarding(options: UseOnboardingOptions = {}): {
  env: Environment;
  step: OnboardingStep;
  permission: NotificationPermissionState;
  /** 권한 요청 등 비동기 작업 진행 중 */
  busy: boolean;
  /** 이번 세션에서 설치가 완료됐는가(appinstalled 수신) */
  installed: boolean;
  /**
   * 설치된 앱이 존재하는가. 이번 세션의 appinstalled 와 브라우저 조회
   * (getInstalledRelatedApps) 를 합친 값이라, 예전에 설치해 둔 경우도 잡힌다.
   *
   * 조회가 불가능한 환경(iOS 등)에서는 이번 세션 설치만 반영된다.
   */
  appInstalled: boolean;
} {
  const { pushProbe } = options;

  const env = useMemo(detectEnvironment, []);
  const { canPrompt, installed, promptInstall } = useInstallPrompt();
  const relatedAppInstalled = useInstalledApp();
  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    readPermission(env)
  );
  const [busy, setBusy] = useState(false);
  /** null = 미확인, true/false = 프로브 결과 */
  const [pushAvailable, setPushAvailable] = useState<boolean | null>(null);

  const request = useCallback(async () => {
    if (!env.supportsNotification) return;
    setBusy(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermissionState);
    } finally {
      setBusy(false);
    }
  }, [env.supportsNotification]);

  // 권한이 허용된 뒤에만 토큰 발급을 시도한다. 허용 전에 부르면 무조건 실패한다.
  useEffect(() => {
    if (permission !== "granted" || !pushProbe) return;
    let cancelled = false;
    void pushProbe()
      .then((ok) => {
        if (!cancelled) setPushAvailable(ok);
      })
      .catch(() => {
        if (!cancelled) setPushAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [permission, pushProbe]);

  const appInstalled = installed || relatedAppInstalled === true;

  const step = useMemo<OnboardingStep>(() => {
    const standalone = env.isStandalone;

    // 1. 브라우저 정규화 — 설치·권한보다 먼저.
    //    이미 앱이 있거나 앱으로 실행 중이면 브라우저를 따질 필요가 없다.
    //    (설치된 사람에게 "다른 브라우저로 여세요" 는 앱을 버리라는 말이 된다)
    if (!standalone && !appInstalled && !env.isPreferredBrowser) {
      if (env.os === "android") {
        return {
          kind: "switch-browser",
          target: "chrome",
          openInChrome: () => {
            window.location.href = buildChromeIntentUrl();
          },
        };
      }
      if (env.os === "ios") {
        // iOS 는 스킴으로 브라우저를 강제할 수 없어 안내만 가능하다.
        return { kind: "switch-browser", target: "safari" };
      }
      if (env.os === "desktop") {
        return { kind: "switch-browser", target: "chrome" };
      }
      return { kind: "push-unavailable", reason: "unsupported-browser" };
    }

    // 2. 설치. iOS 는 홈 화면 추가가 웹푸시의 전제라 반드시 선행한다.
    //    Android 는 미설치 상태에서도 브라우저 푸시가 동작하므로, 설치창을 띄울 수
    //    없으면 막지 않고 권한 단계로 넘어간다.
    if (!standalone && !appInstalled) {
      if (env.os === "ios") return { kind: "install", manual: true };
      if (canPrompt) return { kind: "install", manual: false, promptInstall };
    }

    // 3. 알림 권한
    if (permission === "unsupported") {
      return { kind: "push-unavailable", reason: "unsupported-browser" };
    }
    if (permission === "denied") {
      return { kind: "push-unavailable", reason: "denied" };
    }
    if (permission === "default") {
      return { kind: "enable-notification", request };
    }

    // 4. 권한은 있으나 실제 토큰 발급이 불가한 환경 (GMS 미탑재 등)
    if (pushAvailable === false) {
      return { kind: "push-unavailable", reason: "no-push-service" };
    }

    // 5. 앱은 있는데 브라우저로 들어왔다 — 앱으로 옮겨가도록 안내한다.
    //    알림 권한 뒤에 두는 이유: 권한은 오리진 단위라 브라우저에서 미리 받아두면
    //    설치된 앱이 그대로 물려받는다. 앱에서 권한 팝업을 다시 보지 않아도 된다.
    if (!standalone && appInstalled) return { kind: "open-app", justInstalled: installed };

    // 6. 알림을 정리한 뒤에도 미설치면 수동 설치를 안내한다.
    //    canPrompt 가 false 라 2번을 건너뛴 경로가 여기로 온다 — Chrome 은 설치·제거
    //    직후 beforeinstallprompt 를 한동안 억제하므로 흔히 발생한다.
    //    Android 는 미설치로도 푸시가 오므로 알림보다 뒤에 둔다.
    if (!standalone) return { kind: "install", manual: true };

    return { kind: "done" };
  }, [env, installed, appInstalled, canPrompt, promptInstall, permission, pushAvailable, request]);

  return { env, step, permission, busy, installed, appInstalled };
}
